import { useTranslation } from '@mypoz/i18n';
import { Icon, cn } from '@mypoz/ui';

export interface StepIndicatorProps {
  /** Already-translated step labels, in order. */
  steps: readonly string[];
  /** Zero-based index of the step being shown. */
  current: number;
  /** Which steps are filled in, for their ✓. Without it, the steps before `current` get one. */
  completed?: readonly boolean[];
  onSelect: (index: number) => void;
}

/**
 * The four steps of an order, as a slim numbered stepper (MYP-131).
 *
 * It sits in the title row on a laptop, where the old full-width tab strip took a row of its own.
 * A finished step shows a ✓, the current one a red ring and its name in bold. On a phone only the
 * current step keeps its name on screen; the others are their numbers (still named for a screen
 * reader), so the four fit on one line.
 *
 * Every step stays reachable rather than unlocking one at a time: a draft is reviewed by clicking
 * around it, and a wizard that refuses to show step 3 until step 2 is valid is one nobody can
 * review.
 *
 * Each step carries a `data-tour` mark: the Create order tour (MYP-83) points at it.
 */
export function StepIndicator({ steps, current, completed, onSelect }: StepIndicatorProps) {
  const { t } = useTranslation();

  return (
    <nav aria-label={t('orders.newOrder.steps')}>
      <ol className="flex flex-wrap items-center gap-x-1 gap-y-1 sm:gap-x-2">
        {steps.map((label, index) => {
          const isCurrent = index === current;
          const isDone = !isCurrent && (completed ? completed[index] === true : index < current);

          return (
            <li key={label} className="flex items-center gap-1 sm:gap-2">
              {index > 0 ? (
                <span aria-hidden="true" className="h-px w-3 bg-line-strong sm:w-6" />
              ) : null}
              <button
                type="button"
                data-tour={`order-step-${index}`}
                onClick={() => onSelect(index)}
                aria-current={isCurrent ? 'step' : undefined}
                className={cn(
                  // At least 44px wide too: on a phone a step without its name is just its number.
                  'flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-md px-1 text-sm transition-colors md:justify-start lg:min-h-9 lg:min-w-0',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
                  isCurrent ? 'font-semibold text-ink' : 'text-muted hover:text-ink',
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex size-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold tabular-nums',
                    isDone
                      ? 'border-brand bg-brand text-white'
                      : isCurrent
                        ? 'border-brand text-brand'
                        : 'border-line-strong text-muted',
                  )}
                >
                  {isDone ? <Icon name="check" size={15} /> : index + 1}
                </span>
                <span className={cn(!isCurrent && 'sr-only md:not-sr-only')}>{label}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
